// apps/web/src/lib/offlineQueue.ts
// Offline write queue: mutating requests that fail on NETWORK errors (not
// server rejections) are stored in IndexedDB and replayed when connectivity
// returns. Every queued create carries a client_id, so a replay that already
// landed (e.g. the request succeeded but the response was lost) can't
// duplicate — the server dedupes on (user_id, client_id).
import { API_BASE } from "./apiBase";
import { ensureTokenLoaded, getToken } from "./sessionToken";

const DB_NAME = "caloly-offline";
const STORE = "queue";

export interface QueuedWrite {
  id?: number;
  path: string;
  method: string;
  body: Record<string, unknown>;
  queuedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = run(t.objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  }));
}

function emitChange(): void {
  void queueCount().then((n) =>
    window.dispatchEvent(new CustomEvent("caloly:queue-changed", { detail: { pending: n } })));
}

export async function enqueue(write: Omit<QueuedWrite, "id" | "queuedAt">): Promise<void> {
  await tx("readwrite", (s) => s.add({ ...write, queuedAt: Date.now() }));
  emitChange();
}

export async function queueCount(): Promise<number> {
  try { return await tx("readonly", (s) => s.count()); } catch { return 0; }
}

let replaying = false;

/** Drain the queue oldest-first. Server rejections (4xx/5xx) drop the entry —
 *  it will never succeed and must not wedge the queue; network failures stop
 *  the drain and keep everything for the next attempt. */
export async function replayQueue(): Promise<void> {
  if (replaying) return;
  replaying = true;
  try {
    await ensureTokenLoaded();
    const items = await tx<QueuedWrite[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedWrite[]>);
    let synced = 0;
    for (const item of items.sort((a, b) => (a.id ?? 0) - (b.id ?? 0))) {
      try {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        const token = getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(API_BASE + item.path, {
          method: item.method,
          credentials: "include",
          headers,
          body: JSON.stringify(item.body),
        });
        // Any HTTP response = the server decided; a replay can't fix a 4xx.
        // (401 is left queued: a fresh login can still land these writes.)
        if (res.status === 401) break;
        await tx("readwrite", (s) => s.delete(item.id!));
        synced++;
      } catch {
        break; // still offline — keep the rest for later
      }
    }
    if (synced > 0) {
      emitChange();
      window.dispatchEvent(new CustomEvent("caloly:offline-synced"));
    }
  } finally {
    replaying = false;
  }
}

/** Wire the replay triggers once from the app shell. Returns a cleanup fn. */
export function startOfflineSync(): () => void {
  const onOnline = () => { void replayQueue(); };
  window.addEventListener("online", onOnline);
  void replayQueue(); // boot attempt (app may have been killed while offline)
  let removeResume: (() => void) | null = null;
  if ((window as unknown as { Capacitor?: unknown }).Capacitor) {
    void import("@capacitor/app").then(({ App }) =>
      App.addListener("resume", () => { void replayQueue(); })
        .then((h) => { removeResume = () => h.remove(); }),
    );
  }
  return () => {
    window.removeEventListener("online", onOnline);
    removeResume?.();
  };
}
